import { useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import { ModalLoader } from "../../../Components/Atoms/ModalLoader";
import { ScreenGreeting } from "../../../Components/Molecules";
import { useCardSettings } from "../../../Hooks";
import { formatCurrency } from "../../../Utils/formatters";
import { CardContextActions } from "./components/CardContextActions";
import { CardSelector } from "./components/CardSelector";
import { CardsSummary } from "./components/CardsSummary";
import { CartoesDocumentPanel } from "./components/CartoesDocumentPanel";
import { CartoesQuickActions } from "./components/CartoesQuickActions";
import { CreditLimitCard } from "./components/CreditLimitCard";
import { VirtualCardsGrid } from "./components/VirtualCardsGrid";
import { VoucherBenefitsPanel } from "./components/VoucherBenefitsPanel";

const formatMoney = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return null;
  return formatCurrency(numeric);
};

export const Cartoes = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [activeSection, setActiveSection] = useState("cartoes");
  const [selectedCard, setSelectedCard] = useState("credit");

  const {
    state: cardSettings,
    handleGET: fetchCardSettingsGET,
    loading: settingsLoading,
  } = useCardSettings();

  useEffect(() => {
    fetchCardSettingsGET();
  }, []);

  const hasCard = Object.keys(cardSettings || {}).length > 0;
  const loading = settingsLoading.GET;

  const summary = useMemo(() => {
    if (!hasCard) {
      const creditLimitRaw = 800000;
      const invoiceAmountRaw = 128040;
      const availableCreditRaw = 671960;

      return {
        debitBalance: "R$ 2.450,00",
        creditLimit: "R$ 8.000,00",
        invoiceAmount: "R$ 1.280,40",
        availableCredit: "R$ 6.719,60",
        creditLimitRaw,
        invoiceAmountRaw,
        availableCreditRaw,
      };
    }

    const creditLimitRaw = cardSettings?.credit_limit ?? cardSettings?.limit;

    return {
      debitBalance: formatMoney(cardSettings?.debit_balance ?? cardSettings?.balance),
      creditLimit: formatMoney(creditLimitRaw),
      invoiceAmount: formatMoney(cardSettings?.invoice_amount ?? cardSettings?.current_invoice),
      availableCredit: formatMoney(
        cardSettings?.available_credit ?? cardSettings?.available_limit
      ),
      creditLimitRaw,
      invoiceAmountRaw: cardSettings?.invoice_amount ?? cardSettings?.current_invoice,
      availableCreditRaw:
        cardSettings?.available_credit ?? cardSettings?.available_limit,
    };
  }, [cardSettings, hasCard]);

  const walletCards = useMemo(() => {
    const holderName = cardSettings?.holder_name || "Maria Silva";
    const expiry = cardSettings?.expiry || "09/28";
    const creditLast4 = cardSettings?.mask?.slice(-4) || "4821";
    const debitLast4 = cardSettings?.debit_mask?.slice(-4) || creditLast4;

    return [
      {
        id: "credit",
        type: "credit",
        label: "Crédito",
        holderName,
        last4: creditLast4,
        expiry,
      },
      {
        id: "debit",
        type: "debit",
        label: "Débito",
        holderName,
        last4: debitLast4,
        expiry,
      },
    ];
  }, [cardSettings]);

  const selectedCardData =
    walletCards.find((card) => card.id === selectedCard) || walletCards[0];

  const cardLabel =
    selectedCardData?.type === "credit" ? "Cartão de crédito" : "Cartão de débito";

  const handlePayInvoice = (invoice) => {
    enqueueSnackbar(
      `Pagamento da fatura ${invoice.month} (${formatCurrency(invoice.amountCents)}) em breve.`,
      { variant: "info" }
    );
  };

  return (
    <div className="cartoes-page">
      <ModalLoader open={loading} text="Carregando cartões" />

      <ScreenGreeting
        title="Cartões"
        subTitle="Limite, faturas, extrato, benefícios e virtuais."
      />

      {!hasCard && !loading && (
        <div className="cartoes-demo-banner mb-5">
          <p className="text-sm font-semibold text-[#9a7340]">Pré-visualização</p>
          <p className="mt-1 text-xs text-gray-600">
            Seu cartão ainda não está habilitado. Os dados abaixo são ilustrativos até a
            ativação pelo aplicativo.
          </p>
        </div>
      )}

      <CreditLimitCard
        creditLimit={summary.creditLimit}
        invoiceAmount={summary.invoiceAmount}
        availableCredit={summary.availableCredit}
        creditLimitRaw={summary.creditLimitRaw}
        invoiceAmountRaw={summary.invoiceAmountRaw}
        availableCreditRaw={summary.availableCreditRaw}
        loading={settingsLoading.GET && hasCard}
      />

      <CartoesQuickActions activeSection={activeSection} onSectionChange={setActiveSection} />

      {activeSection === "cartoes" && (
        <div className="space-y-5">
          <CardSelector
            cards={walletCards}
            selectedId={selectedCard}
            onSelect={setSelectedCard}
          />

          <section className="cartoes-overview app-card">
            <div className="cartoes-overview__body">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                Visão geral
              </p>
              <h2 className="mt-1 text-lg font-bold text-brand-dark">{cardLabel}</h2>
              <p className="mt-1 text-sm text-gray-500">
                Final {selectedCardData?.last4} · {selectedCardData?.holderName}
              </p>
              <p className="mt-1 text-xs text-gray-400">
                {selectedCardData?.type === "credit"
                  ? "Use seu limite com controle total."
                  : "Saldo debitado na hora da compra."}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="cartoes-pill cartoes-pill--active">
                  {hasCard ? "Ativo" : "Pendente"}
                </span>
                <span className="cartoes-pill">Contactless</span>
                <span className="cartoes-pill">Internacional</span>
              </div>
            </div>

            <div className="cartoes-overview__actions">
              <CardContextActions />
            </div>
          </section>

          {selectedCardData?.type === "debit" && (
            <CardsSummary
              cardType="debit"
              {...summary}
              loading={settingsLoading.GET && hasCard}
            />
          )}

          <CartoesDocumentPanel
            cardType={selectedCardData?.type}
            cardLabel={cardLabel}
            cardLast4={selectedCardData?.last4}
            onPayInvoice={handlePayInvoice}
          />
        </div>
      )}

      {activeSection === "beneficios" && <VoucherBenefitsPanel />}

      {activeSection === "virtuais" && (
        <div className="app-card p-4 sm:p-5">
          <VirtualCardsGrid />
        </div>
      )}
    </div>
  );
};
